import { ContributorType } from '@risksmart-app/domain/src/types/consts/index';
import type { PendingChangeRequestResponseRow } from '@risksmart-app/trpc/src/types';
import type { ChangeRequestPartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';

/**
 * Owner-display list. `parent.ancestorContributors` is fetched as
 * owner+contributor so the approver-eligibility check (recordApproverResponse)
 * can match the authoritative perm_v2_is_eligible_approver gate — so the panel's
 * owner *display* must re-filter to owners only here. (#6898)
 */
const ownersOnly = <T extends { ContributorType: string | null }>(
  contributors: readonly T[] | null | undefined
): T[] =>
  (contributors ?? []).filter(
    (contributor) => contributor.ContributorType === ContributorType.Owner
  );

/**
 * Map a tRPC change-request row to the GraphQL `ChangeRequestParts` fragment
 * shape that the approval UI and the `useChangeRequests` helpers are typed
 * against. Accepts the pending-by-parent row; the all-statuses by-parent row
 * (`ChangeRequestByParentResponseRow`) is a structural superset and is
 * assignable, so both `useGetPendingChangeRequests` and
 * `useGetChangeRequestsByParent` share this transform (RSP-6087).
 *
 * Two adaptations the raw row needs:
 *   - `parent.owners` is synthesised from the perm_v2 `ancestorContributors`
 *     cache — `getOwners` reads `owners`, but the data-layer row carries
 *     `ancestorContributors`.
 */
export const mapChangeRequestRowsToFragment = (
  rows: PendingChangeRequestResponseRow[]
): ChangeRequestPartsFragment[] =>
  rows.map((row) => ({
    ...row,
    parent: row.parent && {
      ...row.parent,
      owners: ownersOnly(row.parent?.ancestorContributors),
      documentFile: row.parent?.documentFile && {
        ...row.parent.documentFile,
        parent: row.parent.documentFile.parent && {
          ...row.parent.documentFile.parent,
          owners: ownersOnly(
            row.parent.documentFile.parent?.ancestorContributors
          ),
        },
      },
      issue_assessment: row.parent?.issue_assessment && {
        ...row.parent.issue_assessment,
        parent: row.parent.issue_assessment.parent && {
          ...row.parent.issue_assessment.parent,
          owners: ownersOnly(
            row.parent.issue_assessment.parent?.ancestorContributors
          ),
        },
      },
      acceptance: row.parent?.acceptance && {
        ...row.parent.acceptance,
        parents: row.parent.acceptance.parents
          .map((parent) =>
            parent.risk
              ? {
                  risk: {
                    ...parent.risk,
                    owners: ownersOnly(parent.risk?.ancestorContributors),
                  },
                }
              : null
          )
          .filter((parent) => parent !== null),
      },
    },
    responses: row.responses.map((response) => ({
      ...response,
      approver: {
        ...response.approver,
      },
    })),
  }));
